import { cn } from "@/lib/utils";
import { Button } from "../ui/button";
import { Dialog } from "@/components/Dialog";
import { TaskContent } from "@/types/content-types";
import { MutableRefObject, useRef, useState } from "react";
import { drawWrappedText } from "@/utils/canvasUtils";
import { Values } from "./Types";
import { TaskForm } from "./Task";

// The new lines are ignored when the task is shown, but is used when the final image is exported
const questionsMe: TaskContent = [
    {
        body: "# **Jag**",
    },
    {
        body: "Hur mår jag just nu?\n Mentalt-känslomässigt-fysiskt",
        hasTextBox: true,
        lines: 2,
    },
    {
        body: "Vad behöver jag mer av för att\n känna mig levande och i balans?",
        hasTextBox: true,
        lines: 2,
    },
    {
        body: "Hur behandlar jag mig själv i stunder\n av osäkerhet eller trötthet?",
        hasTextBox: true,
        lines: 2,
    },
    {
        body: "Finns det en vana eller ritual som\n får mig att känna mig grundad?",
        hasTextBox: true,
        lines: 2,
    },
];
const questionsFamily: TaskContent = [
    {
        body: "# **Familj**",
    },
    {
        body: "Vilka i min närhet känner jag\n mig mest trygg med och varför?",
        hasTextBox: true,
        lines: 2,
    },
    {
        body: "Vad längtar jag efter i mina\n familjerelationer?",
        hasTextBox: true,
        lines: 2,
    },
    {
        body: "Finns det något jag behöver säga,\n förlåta eller släppa taget om?",
        hasTextBox: true,
        lines: 2,
    },
    {
        body: "På vilket sätt kan och vill jag visa\n mer närvaro i mina nära relationer?",
        hasTextBox: true,
        lines: 2,
    },
];
const questionsRelationships: TaskContent = [
    {
        body: "# **Sammanhang**",
    },
    {
        body: "Var känner jag att jag kan vara mig\n själv utan att behöva anpassa mig?",
        hasTextBox: true,
        lines: 2,
    },
    {
        body: "Hur ser mitt stödjande nätverk ut just nu?",
        hasTextBox: true,
        lines: 2,
    },
    {
        body: "Vilka sammanhang får mig\n att växa och känna mening?",
        hasTextBox: true,
        lines: 2,
    },
    {
        body: "Hur vill jag bidra till min gemenskap?",
        hasTextBox: true,
        lines: 2,
    },
];
const questionsActivities: TaskContent = [
    {
        body: "# **Sysselsättning**",
    },
    {
        body: "Vad ger mig energi och mening\n i det jag gör om dagarna?",
        hasTextBox: true,
        lines: 2,
    },
    {
        body: "Vad skulle jag vilja lägga\n mer eller mindre tid på?",
        hasTextBox: true,
        lines: 2,
    },
    {
        body: "Hur ser balansen ut mellan prestation\n och återhämtning i min vardag?",
        hasTextBox: true,
        lines: 2,
    },
    {
        body: "Känner jag att mitt arbete eller\n mina aktiviteter speglar den jag är?",
        hasTextBox: true,
        lines: 2,
    },
];
const questionsGiveBack: TaskContent = [
    {
        body: "# **Ge tillbaka**",
    },
    {
        body: "När känner jag att jag gör\n skillnad - på riktigt?",
        hasTextBox: true,
        lines: 2,
    },
    {
        body: "Vad vill jag ge mer av till andra\n utan att tappa bort mig själv?",
        hasTextBox: true,
        lines: 2,
    },
    {
        body: "Finns det något jag besitter som andra\n skulle må bra av att få ta del av?",
        hasTextBox: true,
        lines: 2,
    },
    {
        body: "Hur kan jag bidra utan att det måste\n vara stort, perfekt eller synligt?",
        hasTextBox: true,
        lines: 2,
    },
];
const puzzlePieces: { id: PieceId, content: TaskContent, alt: string, imgPath: string, className?: string }[] = [
    { id: 1, content: questionsMe, alt: "Jag", imgPath: "img/puzzle-pieces/jag.png", },
    { id: 2, content: questionsFamily, alt: "Familj", imgPath: "img/puzzle-pieces/familj.png", className: "scale-150", },
    { id: 3, content: questionsRelationships, alt: "Sammanhang", imgPath: "img/puzzle-pieces/sammanhang.png", },
    { id: 4, content: questionsActivities, alt: "Sysselsättning", imgPath: "img/puzzle-pieces/sysselsattning.png", },
    { id: 5, content: questionsGiveBack, alt: "Ge tillbaka", imgPath: "img/puzzle-pieces/ge_tillbaka.png", className: "scale-125", },
];

export type PieceId = number;

const QUESTIONS_PER_PIECE = 4;

export type LifeMapData = {
    completed?: boolean;
    answers: Record<PieceId, Values>;
};
type LifeMapProps = {
    onStateChanged: (state: LifeMapData) => void;
    initialState: LifeMapData;
};
export function LifeMap({ onStateChanged: onSave, initialState }: LifeMapProps) {
    const [data, setData] = useState<LifeMapData>(initialState);
    const pieceRefs = useRef<LifeMapData>({ answers: {} });

    const handleExport = () => {
        const canvas = document.createElement("canvas");
        canvas.width = 1920;
        canvas.height = 1080;
        const ctx = canvas.getContext("2d");

        ctx.fillStyle = "#FFF";
        ctx.fillRect(0, 0, canvas.width, canvas.height);

        // Pieces
        const relativeHeights = [1, 1.5, 1, 0.75, 1.25];
        const totalRelativeHeight = relativeHeights.reduce((acc, num) => acc + num);

        const imgSettings: { element: HTMLImageElement, y: number, width: number, height: number }[] = [];

        let maxWidth = 0;
        for (let i = 1, nextY = 0; i <= 5; i++) {
            const piece = pieceRefs.current[i];
            const imgRatio = piece.width / piece.height;
            const height = canvas.height * relativeHeights[i - 1] / totalRelativeHeight;
            const width = height * imgRatio;

            imgSettings.push({
                element: piece,
                y: nextY,
                width,
                height,
            });

            nextY += height;
            if (width > maxWidth) maxWidth = width;
        }

        imgSettings.forEach((img) => {
            const x = (maxWidth - img.width) / 2;
            ctx.drawImage(img.element, x, img.y, img.width, img.height);
        });

        // Questions

        const height = canvas.height / 5;
        const width = (canvas.width - maxWidth) / 4;
        for (let i = 0, nextY = 0; i < 5; i++, nextY += height) {
            for (let j = 0, nextX = maxWidth + 1; j < 4; j++, nextX += width) {

                // Background
                const questionSectionHeight = height / 3;
                const question = puzzlePieces[i].content[j + 1].body;
                ctx.fillStyle = "hsl(204 100% 25%)";
                ctx.fillRect(nextX, nextY, width - 1, questionSectionHeight);

                // Question texts
                ctx.font = "20px sans-serif";
                ctx.textAlign = "center";
                ctx.fillStyle = "white";

                const questionText = question.toString();
                const textMetrics = ctx.measureText(questionText);

                const ascent = textMetrics.actualBoundingBoxAscent;
                const descent = textMetrics.actualBoundingBoxDescent;
                const textHeight = ascent + descent;
                const yCenterOffset = (ascent - descent) / 2;
                const lineHeight = textHeight * 1.25;

                const questionLines = questionText.split("\n");
                for (let k = 0; k < questionLines.length; k++) {
                    ctx.fillText(
                        questionLines[k],
                        nextX + width / 2,
                        nextY + questionSectionHeight / 2 - (questionLines.length / 2 - 0.5 - k) * lineHeight + yCenterOffset
                    );
                }

                // Users texts
                ctx.textAlign = "left";
                ctx.fillStyle = "black";
                const userLines = data?.answers?.[i + 1]?.[j]?.trim().split("\n");
                if (!userLines) continue;
                let lineCounter = 0;
                for (let k = 0; k < userLines.length; k++) {
                    lineCounter += drawWrappedText(
                        ctx,
                        userLines[k],
                        nextX + 10,
                        nextY + questionSectionHeight + (lineCounter + 1) * lineHeight + yCenterOffset,
                        width,
                        lineHeight
                    );
                }

            }
        }


        // Auto Download
        canvas.toBlob(async (blob) => {
            if (!blob) return;

            // Installed PWAs treat link downloads as navigation to an external
            // view, so prefer the native share sheet where files are supported.
            const file = new File([blob], "din_livskarta.png", { type: "image/png" });
            if (navigator.canShare?.({ files: [file] })) {
                try {
                    await navigator.share({ files: [file] });
                    return;
                } catch (error) {
                    if (error instanceof DOMException && error.name === "AbortError") return;
                }
            }

            const blobUrl = URL.createObjectURL(blob);
            const downloadLink = document.createElement("a");
            downloadLink.download = "din_livskarta.png";
            downloadLink.href = blobUrl;
            document.body.appendChild(downloadLink);
            downloadLink.click();
            downloadLink.remove();
            setTimeout(() => URL.revokeObjectURL(blobUrl), 60_000);
        }, "image/png");
    };

    const checkCompleted = (answers: LifeMapData["answers"]) => {
        const values = Object.values(answers);

        if (values.length !== puzzlePieces.length) return false;

        return values.every(piece =>
            Object.values(piece).every(answer => answer !== "")
        );
    };

    return (
        <div
            className="h-fill relative grow flex flex-col justify-between gap-4 overflow-x-hidden overflow-y-auto"
        >
            {puzzlePieces.map(piece => <PuzzlePiece
                key={piece.id}
                id={piece.id}
                content={piece.content}
                alt={piece.alt}
                imgPath={piece.imgPath}
                onValuesChanged={(values) => {
                    setData((prev) => {
                        const newAnswers = {
                            ...prev?.answers,
                            [piece.id]: values,
                        };
                        const completed = checkCompleted(newAnswers);
                        const newState: LifeMapData = {
                            ...prev,
                            answers: newAnswers,
                            completed: completed,
                        };
                        onSave?.(newState);
                        return newState;
                    });
                }}
                imgRef={pieceRefs}
                onSaved={() => { }}
                onUnsavedChanged={() => { }}
                className={piece.className}
                completed={(() => {
                    const values = Object.values(data?.answers?.[piece.id] ?? { 0: "" });
                    if (values.length !== QUESTIONS_PER_PIECE) return false;
                    return values.every(answer => answer && answer !== "");
                })()
                }
                defaultValues={data?.answers?.[piece.id]}
            />)}
            <Button onClick={handleExport}>Exportera din livskarta</Button>
        </div>
    );
}

export function LifeMapDialog({ initialState, onStateChanged }: LifeMapProps) {
    return (
        <Dialog
            title="Din livskarta"
            trigger={
                <Button
                    variant={initialState?.completed ? "success" : "step-current"}
                >
                    Din livskarta
                </Button>
            }
        >
            <LifeMap
                initialState={initialState}
                onStateChanged={onStateChanged}
            />
        </Dialog>
    );
}


type PuzzlePieceProps = {
    id?: PieceId;
    completed: boolean;
    defaultValues: Values;
    imgPath: string;
    alt: string;
    className?: string;
    content: TaskContent;
    imgRef?: MutableRefObject<Record<PieceId, HTMLImageElement>>;
    onUnsavedChanged?: (unsaved: boolean) => void;
    onSave?: (values: Values) => (void | Promise<void>);
    onSaved?: () => void;
    onValuesChanged?: (values: Values) => void;
};
function PuzzlePiece({ id, imgRef, defaultValues, completed, imgPath, alt, className, content, onValuesChanged, onUnsavedChanged, onSave, onSaved }: PuzzlePieceProps) {
    const [open, setOpen] = useState(false);
    return (
        <div className={cn(
            "h-32 flex flex-col items-center justify-around outline-none",
            className
        )}>
            <Dialog
                open={open}
                onOpenChange={setOpen}
                trigger={
                    <img
                        ref={(element) => {
                            if (!imgRef || !id) return;
                            imgRef.current[id] = element;
                        }}
                        src={imgPath}
                        className={`max-h-full ${!completed && "grayscale"}`}
                        alt={alt}
                    />
                }
            >
                <TaskForm
                    content={content}
                    onSave={onSave}
                    onSaved={() => {
                        onSaved();
                        setOpen(false);
                    }}
                    onUnsavedChanged={onUnsavedChanged}
                    defaultValues={defaultValues}
                    allowEmptyAnswers={true}
                    onValuesChanged={onValuesChanged}
                />
            </Dialog>
        </div>
    );
}